'use client';

import * as React from 'react';

import { Accordion } from '@base-ui/react/accordion';

import { renderInline } from '@/components/article/inline-markdown';

export type FAQItem = {
  question: string;
  answer: string;
};

export function renderAnswer(answer: string) {
  const paragraphs = answer
    .trim()
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);

  return paragraphs.map((paragraph, index) => <p key={index}>{renderInline(paragraph)}</p>);
}

export function FAQ({
  items,
  title = 'Frequently asked questions',
  viewMoreHref,
}: {
  items: readonly FAQItem[];
  title?: string;
  // When set, renders a 'View all FAQ' link below the accordion (mirrors the
  // cosmos lpComponentFaqDisplay.viewMoreLinkUrl field). Same-tab link to
  // preserve the repo convention used by the footer Help Center link.
  viewMoreHref?: string;
}) {
  if (!items?.length) return null;

  return (
    <section className="mt-16 border-t border-line-soft pt-12" aria-labelledby="faq-heading">
      <h2
        id="faq-heading"
        className="font-medium leading-tight tracking-tight text-ink text-subheader-lg sm:text-header-sm"
      >
        {title}
      </h2>
      <Accordion.Root className="mt-8 divide-y divide-line-soft">
        {items.map((item) => (
          <Accordion.Item key={item.question} className="group">
            <Accordion.Header>
              <Accordion.Trigger className="flex w-full items-center justify-between gap-6 py-6 text-left font-medium text-ink transition hover:text-accent text-body-lg">
                <span>{item.question}</span>
                <span
                  className="shrink-0 rounded-full border border-line p-2 text-ink-faint transition group-data-[panel-open]:rotate-45 group-data-[open]:rotate-45"
                  aria-hidden
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M7 1V13M1 7H13"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Panel className="overflow-hidden leading-relaxed text-ink-muted text-body-md transition-all data-[ending-style]:h-0 data-[starting-style]:h-0">
              <div className="space-y-5 pb-6 pr-12">{renderAnswer(item.answer)}</div>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion.Root>
      {viewMoreHref ? (
        <a
          href={viewMoreHref}
          aria-label="View more FAQs"
          className="mt-8 inline-flex items-center gap-2 font-medium text-accent transition hover:opacity-80 text-body-md"
        >
          View all FAQ
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M5 12h14m0 0l-6-6m6 6l-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      ) : null}
    </section>
  );
}
